import { supabase } from "./utility/supabaseClient";

const appbase = import.meta.env.VITE_APP_BASE_URL;

const authProvider = {
  login: async ({ email, password }) => {
    try {
      // sign in with email and password
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return {
          success: false,
          error,
        };
      }

      if (data?.user) {
        try {
          const apiBaseURL = import.meta.env.VITE_API_BASE_URL;
          const response = await fetch(apiBaseURL + "login", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + data.session?.access_token,
            },
            credentials: "include", // to allow cookie setting
          });
          const sessionData = await response.json();
          localStorage.setItem(
            "fungistics-profile",
            JSON.stringify(sessionData)
          );

          if (!response.ok) {
            return {
              success: false,
              error: {
                message: "Backend login failed",
                name: "Cookie not set",
              },
            };
          }

          // 4. Return success if everything worked
          return {
            success: true,
            redirectTo: "/",
          };
        } catch (loginError) {
          return {
            success: false,
            error: loginError,
          };
        }
      }
    } catch (error) {
      return {
        success: false,
        error,
      };
    }

    return {
      success: false,
      error: {
        message: "Login failed",
        name: "Invalid email or password",
      },
    };
  },
  register: async ({ email, password }) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        return {
          success: false,
          error,
        };
      }

      if (data) {
        return {
          success: true,
          redirectTo: "/",
        };
      }
    } catch (error) {
      return {
        success: false,
        error,
      };
    }

    return {
      success: false,
      error: {
        message: "Register failed",
        name: "Invalid email or password",
      },
    };
  },
  forgotPassword: async ({ email }) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${appbase}update-password`,
      });

      if (error) {
        return {
          success: false,
          error,
        };
      }

      if (data) {
        return {
          success: true,
        };
      }
    } catch (error) {
      return {
        success: false,
        error,
      };
    }

    return {
      success: false,
      error: {
        message: "Forgot password failed",
        name: "Invalid email",
      },
    };
  },
  updatePassword: async ({ password }) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password,
      });

      if (error) {
        return {
          success: false,
          error,
        };
      }

      if (data) {
        return {
          success: true,
          redirectTo: "/",
        };
      }
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
    return {
      success: false,
      error: {
        message: "Update password failed",
        name: "Invalid password",
      },
    };
  },
  logout: async () => {
    const { error } = await supabase.auth.signOut();
    localStorage.removeItem("fungistics-profile");

    const apiBaseURL = import.meta.env.VITE_API_BASE_URL;
    await fetch(apiBaseURL + "logout", {
      method: "POST",
      credentials: "include", // to allow cookie setting
    });

    if (error) {
      return {
        success: false,
        error,
      };
    }

    return {
      success: true,
      redirectTo: "/",
    };
  },
  onError: async (error) => {
    console.error(error);
    return { error };
  },
  check: async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const { session } = data;

      if (!session) {
        return {
          authenticated: false,
          error: {
            message: "Check failed",
            name: "Session not found",
          },
          logout: true,
          redirectTo: "/login",
        };
      }
    } catch (error) {
      return {
        authenticated: false,
        error: error || {
          message: "Check failed",
          name: "Not authenticated",
        },
        logout: true,
        redirectTo: "/login",
      };
    }

    return {
      authenticated: true,
    };
  },
  getPermissions: async () => {
    const user = await supabase.auth.getUser();

    if (user) {
      return user.data.user?.role;
    }

    return null;
  },
  getIdentity: async () => {
    const { data } = await supabase.auth.getSession();

    if (data?.session?.user) {
      const raw = localStorage.getItem("fungistics-profile");
      let profile = raw ? JSON.parse(raw) : null;

      if (profile && profile.email !== data.session?.user?.email) {
        profile = null;
      }

      if (!profile) {
        const apiBaseURL = import.meta.env.VITE_API_BASE_URL;
        const response = await fetch(apiBaseURL + "login", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + data.session?.access_token,
          },
          credentials: "include", // to allow cookie setting
        });
        profile = await response.json();
        localStorage.setItem("fungistics-profile", JSON.stringify(profile));

        if (!response.ok) {
          console.error("error logging into API", response) // TK handle
          return null;
        }
      }

      const identity = {
        ...data.session.user,
        name: data?.session?.user?.email,
        profile: profile,
      };
      return identity;
    }

    return null;
  },
};

export default authProvider;
